import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import '@maersk-global/mds-components-core-tag';
import '@maersk-global/mds-components-core-input';
import '@maersk-global/mds-components-core-button';
import { ToastrService } from '../../../services/toastr-service';
import { AemsGeoFilterComponent } from '../../../shared-components/aems-geo-filter/aems-geo-filter.component';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';

@Component({
  selector: 'app-vsc-profile-members',
  imports: [CommonModule, FormsModule, AemsGeoFilterComponent, AemsMultilineTagInputComponent],
  templateUrl: './vsc-profile-members.component.html',
  styleUrl: './vsc-profile-members.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscProfileMembersComponent implements OnChanges {
  @Input() vendorIds: string[] = [];
  @Input() tags: string[] = [];
  @Input() readonly = false;

  @Output() vendorIdsChange = new EventEmitter<string[]>();
  @Output() tagsChange = new EventEmitter<string[]>();

  private readonly toastrService = inject(ToastrService);

  vendorList: string[] = [];
  tagList: string[] = [];

  vendorSearch = '';
  pendingVendorCodes: string[] = [];
  newTagInput = '';
  readonly vendorCodeRegex = /\b[a-zA-Z0-9]{3}\b/g;

  /** Geography node keys, not shop codes — the picker's own selection state. */
  geoSelection: string[] = [];

  /** What the picker last contributed, so a de-selection removes only those codes. */
  private geoContributed: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vendorIds']) {
      this.vendorList = [...this.vendorIds];
      this.vendorSearch = '';
      this.pendingVendorCodes = [];
      this.resetGeoPicker();
    }
    if (changes['tags']) {
      this.tagList = [...this.tags];
      this.newTagInput = '';
    }
  }

  get filteredVendors(): string[] {
    const q = this.vendorSearch.trim().toLowerCase();
    return q ? this.vendorList.filter((v) => v.toLowerCase().includes(q)) : this.vendorList;
  }

  onVendorSearchInput(event: Event): void {
    this.vendorSearch = (event.target as HTMLInputElement).value ?? '';
  }

  onVendorSearchClear(): void {
    this.vendorSearch = '';
  }

  removeVendor(code: string): void {
    this.vendorList = this.vendorList.filter((v) => v !== code);
    this.vendorIdsChange.emit([...this.vendorList]);
    this.toastrService.showToastr(`Vendor code : ${code} will be moved to default settings upon save`, 'info');
  }

  addVendors(): void {
    const newCodes = this.pendingVendorCodes.filter((c) => !this.vendorList.includes(c));
    if (newCodes.length) {
      this.vendorList = [...this.vendorList, ...newCodes];
      this.vendorIdsChange.emit([...this.vendorList]);
    }
    this.pendingVendorCodes = [];
    this.resetGeoPicker();
  }

  /**
   * Mirrors the geography picker's resolved shops into the pending codes. Codes typed by hand are
   * kept, and the codes the picker contributed last time are dropped, so removing a region takes
   * only its own shops out of the field.
   */
  onGeoShopCodesChange(shopCodes: string[]): void {
    const geoCodes = [...new Set(shopCodes.map((code) => code.toUpperCase()))];
    const contributed = new Set(this.geoContributed);
    const manual = this.pendingVendorCodes.filter((code) => !contributed.has(code));

    this.geoContributed = geoCodes;
    this.pendingVendorCodes = [...new Set([...manual, ...geoCodes])];
  }

  private resetGeoPicker(): void {
    this.geoSelection = [];
    this.geoContributed = [];
  }

  removeTag(tag: string): void {
    this.tagList = this.tagList.filter((t) => t !== tag);
    this.tagsChange.emit([...this.tagList]);
  }

  onNewTagInput(event: Event): void {
    this.newTagInput = (event.target as HTMLInputElement).value ?? '';
  }

  addTag(): void {
    const tag = this.newTagInput.trim();
    if (tag && !this.tagList.includes(tag)) {
      this.tagList = [...this.tagList, tag];
      this.tagsChange.emit([...this.tagList]);
    }
    this.newTagInput = '';
  }

  onNewTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.addTag();
    }
  }
}
